import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './EvidenceDetailPage.css';

const EvidenceDetailPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Extract all state information
  const { 
    courseData, 
    courseName,
    fullAlignmentData,
    originalTableData,
    isSingleCourseMode,
    allCoursesData,
    indirectScores,
    surveys,
    selectedSurvey
  } = location.state || {};
  
  const [expandedLG, setExpandedLG] = useState(null);
  const [activeScreenshot, setActiveScreenshot] = useState(null); // {keyword, imageUrl}
  const timeoutRefs = useRef({});
  // For pagination - store current page for each LG
  const [currentPages, setCurrentPages] = useState({});
  // Number of materials to show per page
  const MATERIALS_PER_PAGE = 6;

  // Log state on mount to verify what we received
  useEffect(() => {
    console.log("EvidenceDetailPage mounted with state:", {
      courseData: !!courseData,
      courseName,
      hasFullData: !!fullAlignmentData,
      isSingleCourseMode
    });
  }, [courseData, courseName, fullAlignmentData, isSingleCourseMode]);

  // If no data was passed, try to get it from sessionStorage
  useEffect(() => {
    if (!courseData) {
      try {
        const savedState = sessionStorage.getItem('course_alignment_state');
        if (savedState) {
          const parsedState = JSON.parse(savedState);
          //console.log("Recovered state from sessionStorage");
          
          // Redirect with the recovered state
          navigate(`/evidence-detail${window.location.search}`, {
            state: parsedState,
            replace: true // Replace current history entry to avoid loops
          });
        }
      } catch (err) {
        console.error("Failed to recover state from sessionStorage:", err);
      }
    }
  }, [courseData, navigate]);

  // If no data was passed, redirect back
  if (!courseData) {
    return (
      <div className="evidence-detail-page">
        <div className="error-container">
          <h2>No course data available</h2>
          <button onClick={() => navigate(-1)}>Go Back</button>
        </div>
      </div>
    );
  }

  // Handle back button click - navigate to AssuranceReport with the Course Alignment Overview tab
  const handleBackClick = () => {
    // Get the school parameter from the URL if present
    const urlParams = new URLSearchParams(window.location.search);
    const school = urlParams.get('school') || 'MARYLAND';
    
    // Check if we have the complete state to return with
    const hasCompleteState = !!(fullAlignmentData && originalTableData);
    //console.log("Navigating back with complete state:", hasCompleteState);
    
    // Create the state object to pass back
    const returnState = {
      returnedFromEvidence: true,
      tab: 'course-alignment-overview',
      courseName: courseName,
      
      // Include these only if we have them
      ...(fullAlignmentData && { fullAlignmentData }),
      ...(originalTableData && { originalTableData }),
      ...(typeof isSingleCourseMode !== 'undefined' && { isSingleCourseMode }),
      ...(allCoursesData && { allCoursesData }),
      ...(indirectScores && { indirectScores }),
      ...(surveys && { surveys }),
      ...(selectedSurvey && { selectedSurvey }),
      
      // Always include the original data
      originalData: courseData
    };
    
    // Also store this state in sessionStorage as backup
    try {
      sessionStorage.setItem('return_from_evidence_state', JSON.stringify(returnState));
      //console.log("Saved return state to sessionStorage");
    } catch (err) {
      console.error("Failed to save return state to sessionStorage:", err);
    }
    
    // Determine whether this was for a program or a course
    const isCourse = isSingleCourseMode;
    const path = isCourse ? '/courses-assessment' : '/programs-assessment';
    
    // For program-level assessment, we need to keep the original program name
    // rather than using the course name
    let programName = '';
    if (isCourse) {
      // For course assessment, course name is the program name
      programName = courseName;
    } else {
      // For program assessment, use location state's programName if available
      programName = location.state?.programName || courseName.split('-')[0];  // Try to get program prefix
      
      //console.log(`Returning to program assessment with program name: ${programName}`);
    }
    
    // Navigate back to the appropriate route with all necessary state
    navigate(`${path}?school=${school}`, {
      state: {
        ...returnState,
        showAssuranceReport: true,
        programName: programName,
        courseList: isCourse ? [courseName] : (allCoursesData?.map(course => course.name) || [])
      },
      replace: true // Use replace to avoid adding to history stack
    });
  };

  // Get overall score from LG1 (or the main learning goal)
  const mainLGKey = Object.keys(courseData.alignment_score).find(key => !key.includes('.')) || 
                   Object.keys(courseData.alignment_score)[0];
  
  // Calculate average score from all selected learning goals
  let totalScore = 0;
  let lgCount = 0;
  
  // Get all learning goals with their scores
  const learningGoals = Object.keys(courseData.evidence);
  
  // Calculate average score from all learning goals
  learningGoals.forEach(lg => {
    const lgData = courseData.evidence[lg];
    if (lgData && typeof lgData.overall_score === 'number') {
      totalScore += lgData.overall_score;
      lgCount++;
    }
  });
  
  const overallScore = lgCount > 0 ? totalScore / lgCount : 0;
  
  // Determine the score class based on rubrics
  const getScoreClass = (score) => {
    if (score >= 75) return 'emphasized-score';
    if (score >= 50) return 'reinforced-score';
    if (score >= 25) return 'introduced-score';
    return 'incidental-score';
  };
  
  // Get the score label based on the value
  const getScoreLabel = (score) => {
    if (score >= 75) return 'Emphasized';
    if (score >= 50) return 'Reinforced';
    if (score >= 25) return 'Introduced';
    return 'Covered Incidentally';
  };
  
  const scoreClass = getScoreClass(overallScore);
  const scoreLabel = getScoreLabel(overallScore);
  
  // Sort learning goals: parent LGs first (LG 1), then sub-LGs (LG 1.1)
  const sortedLearningGoals = [...learningGoals].sort((a, b) => {
    // Extract the numeric parts (remove "LG" prefix if present)
    const aClean = a.replace(/^LG\s*/, '');
    const bClean = b.replace(/^LG\s*/, '');
    
    // Split into components
    const aParts = aClean.split('.').map(Number);
    const bParts = bClean.split('.').map(Number);
    
    // Compare first component (main number)
    if (aParts[0] !== bParts[0]) {
      return aParts[0] - bParts[0];
    }
    
    // Same main number, but different depths
    if (aParts.length !== bParts.length) {
      // Parent (shorter) comes first
      return aParts.length - bParts.length;
    }
    
    // Same depth, compare remaining parts
    for (let i = 1; i < aParts.length; i++) {
      if (aParts[i] !== bParts[i]) {
        return aParts[i] - bParts[i];
      }
    }
    
    return 0;
  });
  
  // Debug: Log the sorted learning goals
  //console.log("Original LGs:", learningGoals);
  //console.log("Sorted LGs:", sortedLearningGoals);
  
  // Helper to check if a learning goal is a parent LG (no decimal point)
  const isParentLG = (lg) => !lg.includes('.');

  // Toggle expand/collapse of a learning goal
  const toggleExpand = (lg) => {
    if (expandedLG === lg) {
      setExpandedLG(null);
    } else {
      // Reset pagination for newly expanded LG
      if (!currentPages[lg]) {
        setCurrentPages(prev => ({...prev, [lg]: 0}));
      }
      setExpandedLG(lg);
    }
  };

  // Get the appropriate icon for a material type
  const getMaterialIcon = (materialType) => {
    switch (materialType?.toLowerCase()) {
      case 'pdf':
        return '📄';
      case 'video':
        return '🎥';
      case 'word':
      case 'doc':
      case 'docx':
        return '📝';
      case 'html':
        return '🌐';
      default:
        return '📄';
    }
  };

  // Get the material location from the timestamp link
  const getMaterialLocation = (timestampLink) => {
    if (!timestampLink) return '';
    
    // Extract page information
    if (timestampLink.includes('#page=')) {
      const pageMatch = timestampLink.match(/#page=(\d+)/);
      if (pageMatch && pageMatch[1]) {
        return `Page ${pageMatch[1]}`;
      }
    }
    
    // For videos with timestamp, you would add more logic here
    return '';
  };

  // Handle showing the screenshot
  const handleShowScreenshot = (keyword, screenshots) => {
    //console.log(`Clicked on keyword/concept: ${keyword}`);
    //console.log(`Available screenshots for this material:`, screenshots);
    
    // Find the screenshot URL for this keyword
    const imageUrl = screenshots[keyword];
    
    // Set the active screenshot with both keyword and image URL
    setActiveScreenshot({
      keyword: keyword,
      imageUrl: imageUrl || null
    });
    
    // Clear any existing timeout for this keyword
    if (timeoutRefs.current[keyword]) {
      clearTimeout(timeoutRefs.current[keyword]);
    }
  };
  
  // Handle hiding the screenshot
  const handleHideScreenshot = () => {
    setActiveScreenshot(null);
    
    // Clear any existing timeouts
    Object.keys(timeoutRefs.current).forEach(key => {
      if (timeoutRefs.current[key]) {
        clearTimeout(timeoutRefs.current[key]);
        timeoutRefs.current[key] = null;
      }
    });
  };
  
  // Handle pagination - go to next page
  const nextPage = (lg) => {
    setCurrentPages(prev => ({
      ...prev, 
      [lg]: (prev[lg] || 0) + 1
    }));
  };
  
  // Handle pagination - go to previous page
  const prevPage = (lg) => {
    setCurrentPages(prev => ({
      ...prev, 
      [lg]: Math.max(0, (prev[lg] || 0) - 1)
    }));
  };
  
  // Get materials for current page
  const getPaginatedMaterials = (materials, lg) => {
    const materialEntries = Object.entries(materials);
    const totalPages = Math.ceil(materialEntries.length / MATERIALS_PER_PAGE);
    const currentPage = currentPages[lg] || 0;
    
    // Ensure current page is within bounds
    if (currentPage >= totalPages) {
      setCurrentPages(prev => ({...prev, [lg]: totalPages - 1}));
      return materialEntries.slice(
        (totalPages - 1) * MATERIALS_PER_PAGE,
        totalPages * MATERIALS_PER_PAGE
      );
    }
    
    return materialEntries.slice(
      currentPage * MATERIALS_PER_PAGE,
      (currentPage + 1) * MATERIALS_PER_PAGE
    );
  };

  return (
    <div className="evidence-detail-page">
      <div className="evidence-header">
        <h1>Evidence Details for {courseName}</h1>
        <button className="back-button" onClick={handleBackClick}>
          ← Back
        </button>
      </div>

      <div className="overall-score-container">
        <div className={`score-circle ${scoreClass}`}>
          <div className="score-value">{overallScore.toFixed(1)}</div>
        </div>
        <div className="score-label">Overall Alignment Score</div>
        <div className="score-rating">{scoreLabel}</div>
      </div>

      <div className="evidence-content">
        <div className="learning-goals-table-container">
          <div className="section-header-1">
            <div className="section-header-left">
              <img src="/light.svg" alt="Light" className="section-icon" />
              <h2>Learning Goals Breakdown</h2>
            </div>
            <div className="evidence-count-container">
              <div className="evidence-count-section">
                <div className="evidence-count-label">Items of Evidence</div>
                <div className="evidence-count-value">
                  {(() => {
                    // Count unique materials across all learning goals
                    const allMaterials = new Set();
                    learningGoals.forEach(lg => {
                      const lgData = courseData.evidence[lg];
                      if (lgData && lgData.materials) {
                        Object.keys(lgData.materials).forEach(materialId => {
                          allMaterials.add(materialId);
                        });
                      }
                    });
                    const materialCount = allMaterials.size;
                    return `${materialCount} ${materialCount === 1 ? 'document' : 'documents'}`;
                  })()}
                </div>
              </div>
            </div>
          </div>
          <table className="learning-goals-table">
            <thead>
              <tr>
                <th>LEARNING GOALS</th>
                <th>COVERAGE LEVEL</th>
                <th>SCORE</th>
              </tr>
            </thead>
            <tbody>
              {sortedLearningGoals.map(lg => {
                const lgData = courseData.evidence[lg];

                
                // Determine how many blocks to fill based on score
                const getCoverageLevelBlocks = (score) => {
                  if (score >= 75) return 4; // Emphasized - 4 blocks
                  if (score >= 50) return 3; // Reinforced - 3 blocks
                  if (score >= 25) return 2; // Introduced - 2 blocks
                  return 1; // Covered Incidentally - 1 block
                };
                
                const blocks = getCoverageLevelBlocks(lgData.overall_score);
                const coverageClass = getScoreClass(lgData.overall_score);
                
                // Check if we have materials for this learning goal
                const hasMaterials = Object.keys(lgData.materials || {}).length > 0;
                
                // Get subLGs based on current LG
                const mainLGPrefix = lg.includes('.') ? lg.split('.')[0] : lg;
                const subLGs = learningGoals.filter(subLG => 
                  subLG.startsWith(mainLGPrefix + '.') && subLG !== lg
                );
                
                return (
                  <React.Fragment key={`row-${lg}`}>
                    <tr 
                      className={`${expandedLG === lg ? 'expanded-row' : ''} ${hasMaterials ? 'has-materials' : ''}`}
                    >
                      <td 
                        className={hasMaterials ? 'clickable-cell' : ''}
                        onClick={hasMaterials ? () => toggleExpand(lg) : undefined}
                        style={{ cursor: hasMaterials ? 'pointer' : 'default' }}
                      >
                        {hasMaterials && (
                          <span className="expand-icon">
                            {expandedLG === lg ? '▼' : '▶'}
                          </span>
                        )}
                        <span className={isParentLG(lg) ? 'parent-lg' : ''}>
                          {lg}
                        </span>
                      </td>
                      <td>
                        <div className="coverage-level-container">
                          <div className="coverage-level-blocks">
                            <div className={`block ${blocks >= 1 ? coverageClass : ''}`}></div>
                            <div className={`block ${blocks >= 2 ? coverageClass : ''}`}></div>
                            <div className={`block ${blocks >= 3 ? coverageClass : ''}`}></div>
                            <div className={`block ${blocks >= 4 ? coverageClass : ''}`}></div>
                          </div>
                          <div className="coverage-level-text">{getScoreLabel(lgData.overall_score)}</div>
                        </div>
                      </td>
                      <td className={coverageClass}>
                        {lgData.overall_score.toFixed(1)}
                      </td>
                    </tr>
                    
                    {/* Expanded content */}
                    {expandedLG === lg && (
                      <tr key={`expanded-${lg}`} className="expanded-content">
                        <td colSpan="3">
                          <div className="expanded-container">
                            <table className="materials-table">
                              <thead>
                                <tr>
                                  <th>Course Materials</th>
                                  <th className="center-header">
                                    Keywords
                                    {lgData.keywords_overall_score !== undefined && (
                                      <div className={getScoreClass(lgData.keywords_overall_score)}>
                                        {lgData.keywords_overall_score.toFixed(1)}
                                      </div>
                                    )}
                                  </th>
                                  <th className="center-header">
                                    Concepts
                                    {lgData.concepts_overall_score !== undefined && (
                                      <div className={getScoreClass(lgData.concepts_overall_score)}>
                                        {lgData.concepts_overall_score.toFixed(1)}
                                      </div>
                                    )}
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {getPaginatedMaterials(lgData.materials, lg).map(([materialId, material], index) => {
                                  const materialInfo = material.info_dict || {};
                                  // Debug: Log the material info structure
                                  //console.log(`Material ${materialId} info:`, material.info_dict);
                                  
                                  const materialType = materialInfo['Material Type'] || '';
                                  // REMOVE the next line to avoid duplicate declaration
                                  // const materialUrl = materialInfo['Video URL'] || materialInfo['Downloadable URL'] || '#';
                                  
                                  // Extract title properly, checking various formats of info_dict
                                  let materialTitle = '';
                                  if (materialInfo['Title']) {
                                    // If Title is directly available
                                    materialTitle = materialInfo['Title'];
                                  } else if (Array.isArray(materialInfo) && materialInfo.length > 0) {
                                    // If info_dict is an array
                                    materialTitle = materialInfo[0].Title || '';
                                  } else if (typeof materialInfo === 'object' && Object.keys(materialInfo).length > 0) {
                                    // If info_dict is a nested object
                                    const firstKey = Object.keys(materialInfo)[0];
                                    if (typeof materialInfo[firstKey] === 'object' && materialInfo[firstKey].Title) {
                                      materialTitle = materialInfo[firstKey].Title;
                                    }
                                  }
                                  
                                  // Fallback to material ID if no title found
                                  if (!materialTitle) {
                                    // Try to make the material ID more user-friendly
                                    materialTitle = materialId.includes('/') 
                                      ? materialId.split('/').pop() 
                                      : materialId;
                                  }
                                  
                                  // Get location information from timestamp links
                                  let materialLocation = '';
                                  if (material.segments) {
                                    const firstSegment = Object.values(material.segments)[0];
                                    if (firstSegment && firstSegment.material_link_with_timestamp) {
                                      materialLocation = getMaterialLocation(firstSegment.material_link_with_timestamp);
                                    }
                                  }
                                  
                                  // Get keywords and concepts
                                  const keywords = material.keywords || {};
                                  const concepts = material.concepts || {};
                                  
                                  // Filter to only show keywords and concepts with scores > 0
                                  const activeKeywords = Object.entries(keywords).filter(([_, score]) => score > 0);
                                  const activeConcepts = Object.entries(concepts).filter(([_, score]) => score > 0);
                                  
                                  // Get screenshots for hover (keywords and concepts)
                                  const screenshots = {};
                                  if (material.segments) {
                                    Object.values(material.segments).forEach(segment => {
                                      if (segment.screenshot_dict) {
                                        // Add keyword screenshots
                                        if (segment.screenshot_dict.keyword_dict) {
                                          Object.entries(segment.screenshot_dict.keyword_dict).forEach(([keyword, url]) => {
                                            screenshots[keyword] = url;
                                            //console.log(`Added keyword screenshot for ${keyword}:`, url);
                                          });
                                        }
                                        // Add concept screenshots if available
                                        if (segment.screenshot_dict.concept_dict) {
                                          Object.entries(segment.screenshot_dict.concept_dict).forEach(([concept, url]) => {
                                            screenshots[concept] = url;
                                            //console.log(`Added concept screenshot for ${concept}:`, url);
                                          });
                                        }
                                      }
                                    });
                                  }
                                  // If concept screenshot is missing, but a keyword screenshot exists with the same name, use it
                                  activeConcepts.forEach(([concept]) => {
                                    if (!screenshots[concept] && screenshots[concept]) {
                                      screenshots[concept] = screenshots[concept];
                                      //console.log(`Mapped concept ${concept} to keyword screenshot:`, screenshots[concept]);
                                    }
                                  });
                                  
                                  //console.log(`Final screenshots object for material ${materialId}:`, screenshots);
                                  
                                  // Fix materialUrl to use timestamped link if available
                                  let materialUrl = '#';
                                  if (material.segments) {
                                    const firstSegment = Object.values(material.segments)[0];
                                    if (firstSegment && firstSegment.material_link_with_timestamp) {
                                      materialUrl = firstSegment.material_link_with_timestamp;
                                    }
                                  }
                                  
                                  return (
                                    <tr key={`material-${materialId}-${index}`}>
                                      <td className="material-cell">
                                        <div className="material-info">
                                          <div className="material-info-row">
                                            <span className="material-icon">{getMaterialIcon(materialType)}</span>
                                            <a 
                                              href={materialUrl} 
                                              target="_blank" 
                                              rel="noopener noreferrer"
                                              className="material-link"
                                            >
                                              {materialTitle}
                                            </a>
                                          </div>
                                          {materialLocation && (
                                            <span className="material-location">{materialLocation}</span>
                                          )}
                                        </div>
                                      </td>
                                      <td className="keywords-cell">
                                        <div className="keyword-tags">
                                          {activeKeywords.length > 0 ? activeKeywords.map(([keyword, score]) => (
                                            <div 
                                              key={keyword} 
                                              className={`keyword-tag ${screenshots[keyword] ? 'has-screenshot' : 'no-screenshot'}`}
                                              onClick={screenshots[keyword] ? (e) => {
                                                e.stopPropagation();
                                                handleShowScreenshot(keyword, screenshots);
                                              } : undefined}
                                              style={{ cursor: screenshots[keyword] ? 'pointer' : 'not-allowed' }}
                                            >
                                              <span className="keyword-name">{keyword}</span>
                                            </div>
                                          )) : (
                                            <span className="no-data"></span>
                                          )}
                                        </div>
                                      </td>
                                      <td className="concepts-cell">
                                        <div className="concept-list">
                                          {activeConcepts.length > 0 ? activeConcepts.map(([concept, score]) => (
                                            <div 
                                              key={concept} 
                                              className={`concept-item ${screenshots[concept] ? 'has-screenshot' : 'no-screenshot'}`}
                                              onClick={screenshots[concept] ? (e) => {
                                                e.stopPropagation();
                                                handleShowScreenshot(concept, screenshots);
                                              } : undefined}
                                              style={{ cursor: screenshots[concept] ? 'pointer' : 'not-allowed' }}
                                            >
                                              <span className="concept-text">{concept}</span>
                                            </div>
                                          )) : (
                                            <span className="no-data"></span>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                            
                            {/* Pagination controls */}
                            {Object.keys(lgData.materials).length > MATERIALS_PER_PAGE && (
                              <div className="pagination-controls">
                                <button 
                                  className="pagination-button"
                                  onClick={() => prevPage(lg)}
                                  disabled={(currentPages[lg] || 0) === 0}
                                >
                                  &laquo; Previous
                                </button>
                                
                                <span className="pagination-info">
                                  Page {(currentPages[lg] || 0) + 1} of {Math.ceil(Object.keys(lgData.materials).length / MATERIALS_PER_PAGE)}
                                </span>
                                
                                <button 
                                  className="pagination-button"
                                  onClick={() => nextPage(lg)}
                                  disabled={(currentPages[lg] || 0) >= Math.ceil(Object.keys(lgData.materials).length / MATERIALS_PER_PAGE) - 1}
                                >
                                  Next &raquo;
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Single global screenshot modal */}
      {activeScreenshot && activeScreenshot.imageUrl && (
        <div className="screenshot-preview visible">
          <button 
            className="close-preview" 
            onClick={handleHideScreenshot}
          >
            ×
          </button>
          
          <img 
            src={activeScreenshot.imageUrl} 
            alt={`Preview of ${activeScreenshot.keyword}`}
            onClick={(e) => {
              e.stopPropagation();
              //console.log(`Opening screenshot for ${activeScreenshot.keyword}:`, activeScreenshot.imageUrl);
              window.open(activeScreenshot.imageUrl, '_blank');
            }}
            style={{ cursor: 'pointer' }}
            onError={(e) => {
              console.error(`Failed to load screenshot for ${activeScreenshot.keyword}:`, e);
              e.target.style.display = 'none';
            }}
          />
        </div>
      )}
    </div>
  );
};

export default EvidenceDetailPage; 